import React, { createContext, useContext, useState, useCallback } from 'react';
import { Quote } from '../types/quote';
import { useQuotesDelete } from '@/hooks/quote/useQuotesDelete.tsx';
import { useQuotesUpdate } from '../hooks/useQuotesUpdate';
import { useQuotesCreate } from '../hooks/useQuotesCreate';
import { useQuotes } from '../hooks/useQuotes';
import { useCategories } from '../hooks/useCategories';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

type EditMode = 'none' | 'edit' | 'create';

interface SimpleQuoteContextType {
  quotes: Quote[];
  selectedQuotes: string[];
  editMode: EditMode;
  currentQuote: Quote | null;
  searchQuery: string;
  categoryFilter: string;
  filterUncategorized: boolean;
  showDuplicates: boolean;
  pageSize: number;
  categories: Array<{ id: string; name: string }>;
  isLoading: boolean;
  handleQuoteSelect: (id: string) => void;
  handleQuoteDeselect: (id: string) => void;
  handleQuoteDelete: (id: string) => void;
  handleQuoteUpdate: (quote: Quote) => Promise<void>;
  handleQuoteCreate: (quote: Omit<Quote, 'id'>) => Promise<void>;
  startEditQuote: (quote: Quote) => void;
  startCreateQuote: () => void;
  cancelEdit: () => void;
  setSearchQuery: (query: string) => void;
  setCategoryFilter: (filter: string) => void;
  setFilterUncategorized: (value: boolean) => void;
  setShowDuplicates: (value: boolean) => void;
  setPageSize: (size: number) => void;
  handleBatchDelete: () => void;
}

const SimpleQuoteContext = createContext<SimpleQuoteContextType | undefined>(undefined);

export const SimpleQuoteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [editMode, setEditMode] = useState<EditMode>('none');
  const [currentQuote, setCurrentQuote] = useState<Quote | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [filterUncategorized, setFilterUncategorized] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  // Hooks
  const queryClient = useQueryClient();
  const { quotes = [], isLoading: quotesLoading } = useQuotes();
  const { categories = [], isLoading: categoriesLoading } = useCategories();
  const { mutateAsync: deleteQuote } = useQuotesDelete({ queryClient });
  const { mutate: updateQuote } = useQuotesUpdate();
  const { mutate: createQuote } = useQuotesCreate();

  // Handlers
  const handleQuoteSelect = useCallback((id: string) => {
    setSelectedQuotes(prev => [...prev, id]);
  }, []);

  const handleQuoteDeselect = useCallback((id: string) => {
    setSelectedQuotes(prev => prev.filter(quoteId => quoteId !== id));
  }, []);

  const handleQuoteDelete = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      await deleteQuote(id);
      setSelectedQuotes((prev) => prev.filter((quoteId) => quoteId !== id));
      toast({
        title: "Success",
        description: "Quote deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete quote",
      });
    } finally {
      setIsLoading(false);
    }
  }, [deleteQuote, toast]);

  const handleQuoteUpdate = useCallback(async (quote: Quote) => {
    await updateQuote(quote);
    setEditMode('none');
    setCurrentQuote(null);
  }, [updateQuote]);

  const handleQuoteCreate = useCallback(async (quote: Omit<Quote, 'id'>) => {
    await createQuote(quote);
    setEditMode('none');
    setCurrentQuote(null);
  }, [createQuote]);

  const startEditQuote = useCallback((quote: Quote) => {
    setEditMode('edit');
    setCurrentQuote(quote);
  }, []);

  const startCreateQuote = useCallback(() => {
    setEditMode('create');
    setCurrentQuote(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditMode('none');
    setCurrentQuote(null);
  }, []);

  const handleBatchDelete = useCallback(() => {
    if (selectedQuotes.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedQuotes.length} quotes?`)) {
      selectedQuotes.forEach(id => {
        deleteQuote(id);
      });
      setSelectedQuotes([]);
      toast({
        title: "Quotes deleted",
        description: `Successfully deleted ${selectedQuotes.length} quotes.`,
      });
    }
  }, [selectedQuotes, deleteQuote]);

  return (
    <SimpleQuoteContext.Provider
      value={{
        quotes,
        selectedQuotes,
        editMode,
        currentQuote,
        searchQuery,
        categoryFilter,
        filterUncategorized,
        showDuplicates,
        pageSize,
        categories,
        isLoading: quotesLoading || categoriesLoading || isLoading,
        handleQuoteSelect,
        handleQuoteDeselect,
        handleQuoteDelete,
        handleQuoteUpdate,
        handleQuoteCreate,
        startEditQuote,
        startCreateQuote,
        cancelEdit,
        setSearchQuery,
        setCategoryFilter,
        setFilterUncategorized,
        setShowDuplicates,
        setPageSize,
        handleBatchDelete
      }}
    >
      {children}
    </SimpleQuoteContext.Provider>
  );
};

export const useSimpleQuoteContext = () => {
  const context = useContext(SimpleQuoteContext);
  if (!context) {
    throw new Error('useSimpleQuoteContext must be used within a SimpleQuoteProvider');
  }
  return context;
};
